import { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import CustomerHeader from "@/components/CustomerHeader";
import { Upload, X, ArrowRight, Coins, Sparkles, Loader2, Shuffle } from "lucide-react";
import { toast } from "sonner";

const ZODIACS = ["Koç", "Boğa", "İkizler", "Yengeç", "Aslan", "Başak", "Terazi", "Akrep", "Yay", "Oğlak", "Kova", "Balık"];
const DREAM_SCHOOLS = [
  { value: "islami", label: "İslami Tabir" },
  { value: "freud", label: "Freud (Psikoanaliz)" },
  { value: "jung", label: "Jung (Arketip)" },
  { value: "genel", label: "Genel / Halk" },
];

function shuffleArr(arr) {
  const a = arr.map((x, i) => ({ x, i }));
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a; // [{x: card, i: originalIndex}]
}

function PhotoSlot({ label, value, onChange, testid }) {
  const ref = useRef(null);
  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) return toast.error("JPG/PNG/WEBP kabul edilir");
    if (f.size > 6 * 1024 * 1024) return toast.error("Max 6MB");
    const r = new FileReader();
    r.onload = () => onChange(r.result);
    r.readAsDataURL(f);
  };
  return (
    <div>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFile} className="hidden" data-testid={testid} />
      {value ? (
        <div className="relative rounded-xl overflow-hidden gold-border aspect-square">
          <img src={value} alt={label} className="w-full h-full object-cover" />
          <button onClick={() => onChange(null)} className="absolute top-1.5 right-1.5 bg-black/70 rounded-full p-1 hover:bg-black">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} className="aspect-square rounded-xl border-2 border-dashed border-[#D4AF37]/40 hover:border-[#D4AF37] transition-colors flex flex-col items-center justify-center gap-2 text-white/60 hover:text-white bg-white/[0.02] w-full">
          <Upload className="w-6 h-6 gold-text" />
          <div className="text-xs">{label}</div>
        </button>
      )}
    </div>
  );
}

export default function FortuneFlow() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user, refreshUser } = useAuth();
  const { t } = useLang();
  const [ft, setFt] = useState(null);
  const [deck, setDeck] = useState({ tarot: [], katina: [], iskambil: [] });
  const [imgB64, setImgB64] = useState(null);
  const [imgB64_2, setImgB64_2] = useState(null);
  const [imgB64_3, setImgB64_3] = useState(null);
  const [text, setText] = useState("");
  const [question, setQuestion] = useState("");
  const [dreamSchool, setDreamSchool] = useState("islami");
  const [selectedCards, setSelectedCards] = useState([]);
  const [spread, setSpread] = useState("3-card");
  const [shuffleSeed, setShuffleSeed] = useState(0);
  const [personA, setPersonA] = useState("");
  const [personB, setPersonB] = useState("");
  const [zodiac, setZodiac] = useState(user?.zodiac_sign || "");
  const [loading, setLoading] = useState(false);
  const [waiting, setWaiting] = useState(0);

  useEffect(() => {
    api.get(`/fortune-types/${slug}`).then(({ data }) => setFt(data)).catch(() => toast.error("Fal türü bulunamadı"));
    api.get("/tarot-deck").then(({ data }) => setDeck(data));
    // Reset when navigating
    setSelectedCards([]); setImgB64(null); setImgB64_2(null); setImgB64_3(null); setText(""); setQuestion("");
    setShuffleSeed(Math.random());
  }, [slug]);

  const spreadCount = { "3-card": 3, "5-card": 5, "10-card": 10 }[spread] || 3;
  const iskambilCount = 5;
  const cardsSource = slug === "tarot" ? deck.tarot : slug === "katina" ? deck.katina : deck.iskambil;
  const requiredCards = slug === "tarot" || slug === "katina" ? spreadCount : iskambilCount;

  // Shuffled deck order — reshuffles on load / manual click
  const shuffled = useMemo(() => shuffleArr(cardsSource || []), [cardsSource, shuffleSeed]);

  const toggleCard = (originalIdx) => {
    if (selectedCards.includes(originalIdx)) setSelectedCards(selectedCards.filter((x) => x !== originalIdx));
    else if (selectedCards.length < requiredCards) setSelectedCards([...selectedCards, originalIdx]);
  };

  const randomPick = () => {
    const indices = shuffled.slice(0, requiredCards).map((c) => c.i);
    setSelectedCards(indices);
  };

  const submit = async () => {
    if (!ft) return;
    if (ft.requires_photo && !imgB64) return toast.error("En az bir fotoğraf yükle");
    if (ft.requires_text && !text && !question) return toast.error("Metin gir");
    if (ft.requires_cards && selectedCards.length !== requiredCards) return toast.error(`${requiredCards} kart seç`);
    if (ft.requires_two_names && (!personA || !personB)) return toast.error("İki isim gir");
    if (ft.requires_zodiac && !zodiac) return toast.error("Burç seç");

    setLoading(true);
    setWaiting(ft.wait_seconds || 8);
    const timer = setInterval(() => setWaiting((w) => (w > 0 ? w - 1 : 0)), 1000);

    try {
      const payload = {
        fortune_type_slug: slug,
        lang: (typeof window !== "undefined" && localStorage.getItem("falzilla_lang")) || "tr",
        image_base64: imgB64,
        image_base64_2: imgB64_2,
        image_base64_3: imgB64_3,
        text_input: text || undefined,
        question: question || undefined,
        dream_school: ft.requires_dream_school ? dreamSchool : undefined,
        tarot_cards: ft.requires_cards ? selectedCards : undefined,
        tarot_spread: (slug === "tarot" || slug === "katina") ? spread : undefined,
        person_a: ft.requires_two_names ? personA : undefined,
        person_b: ft.requires_two_names ? personB : undefined,
        zodiac: ft.requires_zodiac ? zodiac : undefined,
      };
      const { data } = await api.post("/fortunes", payload);
      clearInterval(timer);
      await refreshUser();
      nav(`/sonuc/${data.id}`);
    } catch (err) {
      clearInterval(timer);
      toast.error(err.response?.data?.detail || "Fal oluşturulamadı");
    } finally {
      setLoading(false);
      setWaiting(0);
    }
  };

  if (!ft) return (
    <div className="cosmic-bg min-h-screen"><div className="stars" /><CustomerHeader /><div className="text-center py-20 text-white/60">{t("common.loading")}</div></div>
  );

  const isCardBased = ft.requires_cards;
  const hasImages = slug === "tarot" || slug === "katina"; // decks with images

  return (
    <div className="cosmic-bg min-h-screen">
      <div className="stars" />
      <CustomerHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/panel" className="text-white/60 text-sm hover:gold-text" data-testid="back-to-dashboard">← Panele dön</Link>

        <div className="mt-4 mb-8">
          <h1 className="font-serif-myst text-4xl md:text-5xl">{ft.name_tr}</h1>
          <p className="text-white/60 mt-2">{ft.description}</p>
          <div className="mt-3 inline-flex items-center gap-1.5 token-pill px-3 py-1 rounded-full text-xs">
            <Coins className="w-3.5 h-3.5" /> {t("fortune.tokenCost", { n: ft.token_cost })}
          </div>
        </div>

        {loading && (
          <div className="glass-strong rounded-3xl p-10 text-center fade-up">
            <div className="relative w-32 h-32 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full gold-border animate-pulse-gold" />
              <Sparkles className="absolute inset-0 m-auto w-14 h-14 gold-text spin-slow" />
            </div>
            <h2 className="font-serif-myst text-3xl">{t("fortune.waiting.title")}</h2>
            <p className="text-white/60 mt-2">{t("fortune.waiting.subtitle")}</p>
            <div className="mt-4 text-6xl font-serif-myst gold-text tabular-nums">{waiting}</div>
            <Loader2 className="w-5 h-5 mx-auto mt-4 animate-spin text-white/40" />
          </div>
        )}

        {!loading && (
          <div className="glass rounded-3xl p-6 md:p-8 space-y-6">
            {ft.requires_photo && (
              <div>
                <label className="block text-sm font-medium mb-2">{t("fortune.uploadPhoto")}</label>
                <p className="text-xs text-white/50 mb-3">{t("fortune.uploadPhotoDesc")}</p>
                <div className="grid grid-cols-3 gap-3">
                  <PhotoSlot label={t("fortune.uploadAngle", { n: 1 }) + " *"} value={imgB64} onChange={setImgB64} testid="photo-1-input" />
                  <PhotoSlot label={t("fortune.uploadAngle", { n: 2 })} value={imgB64_2} onChange={setImgB64_2} testid="photo-2-input" />
                  <PhotoSlot label={t("fortune.uploadAngle", { n: 3 })} value={imgB64_3} onChange={setImgB64_3} testid="photo-3-input" />
                </div>
              </div>
            )}

            {ft.requires_dream_school && (
              <div>
                <label className="block text-sm font-medium mb-3">{t("fortune.selectSchool")}</label>
                <div className="grid grid-cols-2 gap-2">
                  {DREAM_SCHOOLS.map((s) => (
                    <button key={s.value} type="button" onClick={() => setDreamSchool(s.value)} data-testid={`dream-school-${s.value}`}
                      className={`px-4 py-2.5 rounded-xl text-sm transition-colors border ${dreamSchool === s.value ? "gold-border gold-text bg-[#D4AF37]/10" : "border-white/10 text-white/70 hover:border-white/30"}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {ft.requires_text && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  {slug === "ruya" ? t("fortune.dreamText") : t("fortune.question")}
                </label>
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} data-testid="text-input"
                  placeholder={slug === "ruya" ? "Rüyanı detaylıca yaz..." : "Aklından geçen soru..."}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/50" />
              </div>
            )}

            {isCardBased && (
              <div>
                {(slug === "tarot" || slug === "katina") && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">{t("fortune.spreadType")}</label>
                    <div className="flex gap-2">
                      {["3-card", "5-card", "10-card"].map((s) => (
                        <button key={s} type="button" onClick={() => { setSpread(s); setSelectedCards([]); }} data-testid={`spread-${s}`}
                          className={`px-4 py-2 rounded-full text-sm border transition-colors ${spread === s ? "gold-border gold-text bg-[#D4AF37]/10" : "border-white/10 text-white/70 hover:border-white/30"}`}>
                          {s === "3-card" ? "3 Kart" : s === "5-card" ? "5 Kart" : "10 Kart"}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="text-sm text-white/70">{t("fortune.selectedCards", { n: selectedCards.length, total: requiredCards })}</div>
                  <div className="flex gap-2">
                    <button onClick={() => { setShuffleSeed(Math.random()); setSelectedCards([]); }} data-testid="shuffle-btn" className="text-xs gold-text hover:underline flex items-center gap-1">
                      <Shuffle className="w-3 h-3" /> {t("fortune.reshuffle")}
                    </button>
                    <button onClick={randomPick} data-testid="random-pick-btn" className="text-xs gold-text hover:underline">
                      {t("fortune.randomPick")}
                    </button>
                  </div>
                </div>

                <div className={`grid ${hasImages ? "grid-cols-4 sm:grid-cols-6" : "grid-cols-5 sm:grid-cols-8"} gap-2 max-h-[500px] overflow-y-auto pr-2`}>
                  {shuffled.map(({ x: c, i: originalIdx }) => {
                    const sel = selectedCards.includes(originalIdx);
                    const order = selectedCards.indexOf(originalIdx);
                    if (hasImages && c.img) {
                      return (
                        <button
                          key={`${originalIdx}-${shuffleSeed}`}
                          type="button"
                          onClick={() => toggleCard(originalIdx)}
                          data-testid={`card-${originalIdx}`}
                          className={`relative aspect-[2/3] rounded-lg overflow-hidden border-2 transition-all ${sel ? "border-[#D4AF37] scale-105 shadow-lg shadow-[#D4AF37]/40" : "border-white/10 hover:border-white/30"}`}
                        >
                          {sel ? (
                            <>
                              <img src={c.img} alt={c.n} className="w-full h-full object-cover" loading="lazy" />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
                              <div className="absolute bottom-1 left-1 right-1 text-center">
                                <div className="gold-text font-serif-myst text-[11px] leading-tight font-semibold">{c.n}</div>
                              </div>
                              <div className="absolute top-1 right-1 bg-[#D4AF37] text-black text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                                {order + 1}
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-[#1a1f3a] via-[#0F1320] to-[#1a1f3a] flex items-center justify-center relative">
                              <div className="absolute inset-2 border border-[#D4AF37]/30 rounded" />
                              <Sparkles className="w-6 h-6 gold-text opacity-40" />
                            </div>
                          )}
                        </button>
                      );
                    }
                    // Iskambil / non-image fallback
                    return (
                      <button
                        key={`${originalIdx}-${shuffleSeed}`}
                        type="button"
                        onClick={() => toggleCard(originalIdx)}
                        data-testid={`card-${originalIdx}`}
                        className={`aspect-[2/3] rounded-lg text-[10px] leading-tight p-1 transition-colors ${sel ? "gold-border bg-gradient-to-br from-[#D4AF37]/30 to-[#D4AF37]/5 gold-text font-semibold" : "bg-white/5 border border-white/10 text-white/50 hover:border-white/30"}`}
                      >
                        {sel ? c.n || c : "?"}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {ft.requires_two_names && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-white/60 mb-1 block">1. İsim</label>
                  <input value={personA} onChange={(e) => setPersonA(e.target.value)} data-testid="person-a-input" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]/50" />
                </div>
                <div>
                  <label className="text-xs text-white/60 mb-1 block">2. İsim</label>
                  <input value={personB} onChange={(e) => setPersonB(e.target.value)} data-testid="person-b-input" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]/50" />
                </div>
              </div>
            )}

            {ft.requires_zodiac && (
              <div>
                <label className="block text-sm font-medium mb-3">{t("fortune.selectZodiac")}</label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {ZODIACS.map((z) => (
                    <button key={z} type="button" onClick={() => setZodiac(z)} data-testid={`zodiac-${z}`}
                      className={`px-3 py-2 rounded-xl text-sm border transition-colors ${zodiac === z ? "gold-border gold-text bg-[#D4AF37]/10" : "border-white/10 text-white/70 hover:border-white/30"}`}>
                      {z}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(ft.requires_photo || ft.requires_cards) && !ft.requires_text && (
              <div>
                <label className="text-xs text-white/60 mb-1 block">{t("fortune.optionalQuestion")}</label>
                <input value={question} onChange={(e) => setQuestion(e.target.value)} data-testid="question-input" placeholder="Yorumu neye odaklamak istersin?" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/50" />
              </div>
            )}

            <button onClick={submit} disabled={loading || (user && user.tokens < ft.token_cost && !user.is_premium)} data-testid="submit-fortune-btn" className="btn-gold w-full py-3.5 rounded-xl text-base flex items-center justify-center gap-2">
              {t("fortune.submit")} <ArrowRight className="w-4 h-4" />
            </button>
            {user && user.tokens < ft.token_cost && !user.is_premium && (
              <div className="text-center text-sm text-red-300">
                {t("fortune.insufficient")} <Link to="/magaza" className="gold-text hover:underline">{t("fortune.getTokens")}</Link>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
